import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import * as Sentry from '@sentry/react';

// Extend Window interface for Sentry
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: Error, context?: any) => void;
    };
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary component to catch rendering errors and display a fallback UI
 * instead of crashing the entire application with a white screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Report to Sentry via the npm SDK. The previous `window.Sentry` check never
    // fired — @sentry/react's init does not set window.Sentry (only the CDN bundle
    // does) — so caught errors were never reported. See BLUM-6399.
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default error UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          padding={3}
        >
          <Paper elevation={3} style={{ padding: '32px', maxWidth: '600px', textAlign: 'center' }}>
            <WarningIcon style={{ fontSize: 64, color: '#f44336', marginBottom: '16px' }} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              We encountered an error while loading this page. This could be due to missing or unexpected data.
            </Typography>
            {this.state.error && (
              <Typography variant="body2" color="error" paragraph style={{ marginTop: '16px' }}>
                Error: {this.state.error.message}
              </Typography>
            )}
            <Box marginTop={3} display="flex" justifyContent="center" gridGap={2}>
              <Button variant="outlined" color="primary" onClick={this.handleReset}>
                Try Again
              </Button>
              <Button variant="contained" color="primary" onClick={this.handleReload}>
                Reload Page
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
